import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn, loading } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user types
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    setAuthError('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData?.password?.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setAuthError('');
      const { error } = await signIn(formData?.email, formData?.password);
      
      if (error) {
        // Handle specific Supabase auth errors
        if (error?.message?.includes('Invalid login credentials')) {
          setAuthError('Invalid email or password. Please check your credentials.');
        } else if (error?.message?.includes('Email not confirmed')) {
          setAuthError('Please check your email and confirm your account first.');
        } else if (error?.message?.includes('Too many requests')) {
          setAuthError('Too many login attempts. Please wait a moment and try again.');
        } else if (error?.message?.includes('Failed to fetch') || 
                   error?.message?.includes('AuthRetryableFetchError')) {
          setAuthError('Cannot connect to authentication service. Please check your connection and try again.');
        } else {
          setAuthError(error?.message || 'Login failed. Please try again.');
        }
        return;
      }

      // Success - user will be redirected by auth state change
      
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Authentication Error Display */}
        {authError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 [data-theme='dark'] &:bg-red-900/20 [data-theme='dark'] &:border-red-800">
            <p className="text-red-800 text-sm [data-theme='dark'] &:text-red-300">{authError}</p>
            {authError?.includes('connection') && (
              <div className="mt-2 text-xs text-red-600 [data-theme='dark'] &:text-red-400">
                <p>• Check your internet connection</p>
                <p>• Verify that the authentication service is available</p>
                <p>• Try again in a few moments</p>
              </div>
            )}
          </div>
        )}

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            Email Address
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            disabled={loading}
            icon={Mail}
            autoComplete="email"
          />
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
            Password
          </label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              disabled={loading}
              icon={Lock}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
              disabled={loading}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full"
          loading={loading}
          disabled={loading}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>

        {/* Additional Options */}
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 transition-colors [data-theme='dark'] &:text-blue-400 [data-theme='dark'] &:hover:text-blue-300"
            disabled={loading}
          >
            Forgot Password?
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={loading}
          >
            Need Help?
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;